import React from 'react';
import { Form, Field } from 'react-final-form';
import { Trans, i18nMark } from '@lingui/react';
import { FORM_ERROR } from 'final-form';

import { login } from '../api';
import { validateEmail } from '../form-helpers';
import AppLogo from './utils/AppLogo';

function validateLoginForm(values) {
    const errors = {};

    if (!values.email) {
        errors.email = i18nMark('validation:error:email:mandatory');
    } else if (!validateEmail(values.email)) {
        errors.email = i18nMark('validation:error:email:invalid');
    }

    if (!values.password) {
        errors.password = i18nMark('validation:error:password');
    }

    return errors;
}

const PublicArticleLoginForm = ({ i18n, onLoginSuccess }) => {

    const onLoginSubmit = async (data) => {
        try {
            const res = await login(data);
            onLoginSuccess(res.data);
        } catch (err) {
            let errorMessage = i18n._('error:unexpected');

            if (err.response) {
                if (err.response.status === 401 || err.response.status === 412) {
                    errorMessage = i18n._('error:login:invalid');
                } else if (err.response.status === 403) {
                    errorMessage = i18n._('error:login:user:disabled');
                }
            } else if (err.request) {
                errorMessage = i18n._('error:server:generic');
            }

            return {
                [FORM_ERROR]: errorMessage,
            };
        }
    };

    return (
        <div className="mb-3 p-4 md:pt-0 md:pb-0 md:pl-0 d-flex flex-col md:flex-row items-start md:items-center"
             style={
            {
                backgroundColor: '#e0e0e0',
                borderRadius: '0',
                height: '100%',
            }
        }
        >
            <div className="text-center md:mr-4 pb-2 md:p-2">
                <AppLogo />
            </div>
            <h6
                className="text-left md:mr-3 md:mb-0 md:ml-auto"
                style={{
                    fontSize: '14px',
                    fontWeight: '400',
                }}
            >
                <Trans id="login:to:view:details" />
            </h6>
            <Form
                onSubmit={onLoginSubmit}
                validate={validateLoginForm}
                render={({
                    handleSubmit,
                    pristine,
                    submitError,
                    submitting,
                }) => (
                    <form
                        className="w-full md:w-auto"
                        onSubmit={handleSubmit}>
                        <div className="d-flex flex-col md:flex-row items-start md:items-center gap-2">
                            <Field name="email">
                                {({ input, meta }) => (
                                    <div className="form-group w-full md:w-auto mb-4 md:!mb-0 flex-grow-1">
                                        <label htmlFor="email" className="d-none">
                                            <Trans id="email" />
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Email"
                                            className={`form-input input-sm w-full ${(meta.touched || meta.submitFailed) && meta.error ? 'is-error' : ''}`}
                                            {...input}
                                        />
                                        {(meta.touched || meta.submitFailed) && meta.error && (
                                            <p
                                                className="form-input-hint text-error"
                                                style={{
                                                    position: 'absolute',
                                                    maxWidth: '175px',
                                                    lineHeight: '1',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                <Trans id={meta.error} />
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Field>
                            <Field name="password">
                                {({ input, meta }) => (
                                    <div className="form-group w-full md:w-auto mb-0 flex-grow-1">
                                        <label htmlFor="password" className="d-none">
                                            <Trans id="password" />
                                        </label>
                                        <input
                                            type="password"
                                            placeholder="Password"
                                            className={`form-input input-sm ${(meta.touched || meta.submitFailed) && meta.error ? 'is-error' : ''}`}
                                            {...input}
                                        />
                                        {(meta.touched || meta.submitFailed) && meta.error && (
                                            <p
                                                className="form-input-hint text-error"
                                                style={{
                                                    position: 'absolute',
                                                    maxWidth: '175px',
                                                    lineHeight: '1',
                                                    transform: 'translateY(-25%)',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                <Trans id={meta.error} />
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Field>
                            <button
                                type="submit"
                                className="btn btn-primary btn-sm self-start w-full md:w-auto"
                                disabled={pristine || submitting}
                            >
                                {submitting ? (
                                    <span className="loading" />
                                ) : (
                                    <Trans id="enter" />
                                )}
                            </button>
                        </div>
                        {submitError && (
                            <div className="bg-error mt-2 p-2 text-center">
                                {submitError}
                            </div>
                        )}
                    </form>
                )}
            />
        </div>
    );
};

export default PublicArticleLoginForm;